import { formatMoney } from "components/organisms/FormattedCurrencyText";
import React from "react";
import { Button, CardFullMobileModal, ModalProps } from "@jasperlabs/jux-next";
import { useParams, useRouter } from "next/navigation";
import { OfferDigitalSubscription } from "types";
import RenderIfCondition from "components/organisms/RenderIfCondition";
import { TenantSupportLink } from "tenants/components";
import CurrentlyInvestingAs from "components/organisms/CurrentlyInvestingAs";
import { gql } from "@apollo/client";
import { AllocationRequestFragment } from "./RequestAllocationCard.generated";
import useIsAllocationDisabled from "../place-allocation/useIsAllocationDisabled";

gql`
	fragment AllocationRequest on Offer {
		id
		status
		digitalSubscription
		minInvestment
		minUnitsPerInvestmentDecimal {
			value
			decimalPlaces
		}
		pricePerUnit
		fund {
			id
			name
			currency {
				...FormattedCurrencyTextCurrencyFields
			}
		}
	}
`;

const AllocationDetails = ({ offer }: { offer: AllocationRequestFragment }) => {
	const { minUnitsPerInvestmentDecimal, fund, pricePerUnit } = offer;

	const offerDetailsRow = [
		{
			label: "Fund",
			value: fund.name,
		},
		{
			label: "Min. investment",
			value: formatMoney({
				currency: fund.currency,
				value:
					Number(minUnitsPerInvestmentDecimal?.value ?? 0) *
					Number(pricePerUnit),
			}),
		},
		{
			label: "Price per unit",
			value: formatMoney({
				currency: fund.currency,
				value: pricePerUnit ?? 0,
			}),
		},
	];
	return (
		<div>
			{offerDetailsRow.map((detail) => (
				<div
					key={detail.label}
					className="flex flex-row items-baseline justify-between gap-2 border-b border-solid border-neutral-200 py-3 last:border-b-0"
				>
					<div className="text-body-sm text-color-body-muted">
						{detail.label}
					</div>
					<div className="text-body-default text-color-body text-balance text-right">
						{detail.value}
					</div>
				</div>
			))}
		</div>
	);
};

export function RequestAllocationDesktop({
	offer,
}: {
	offer: AllocationRequestFragment;
}) {
	const router = useRouter();
	const params = useParams<{ slug: string }>();

	const isAllocationDisabled = useIsAllocationDisabled();

	return (
		<div
			data-testid="allocation-request-form"
			className="border border-neutral-200 rounded-md"
		>
			<div className="border-b border-neutral-200 text-subheading-default text-center py-4">
				Request Allocation
			</div>
			<div className="grid gap-y-3 p-6">
				<AllocationDetails offer={offer} />
				<RenderIfCondition
					condition={!isAllocationDisabled}
					fallback={
						<TenantSupportLink>
							<Button data-testid="contact-us-button" fullWidth>
								Contact Us
							</Button>
						</TenantSupportLink>
					}
				>
					<Button
						data-testid="offer-invest-button"
						fullWidth
						onClick={() => {
							router.push(`/invest/${params.slug}/place-allocation`);
						}}
					>
						Invest
					</Button>
				</RenderIfCondition>
			</div>
		</div>
	);
}

export function RequestAllocationMobile({
	offer,
	...rest
}: {
	offer: AllocationRequestFragment;
} & ModalProps) {
	const router = useRouter();
	const params = useParams<{ slug: string }>();

	const isDigitalSubscriptionDisabled =
		offer.digitalSubscription === OfferDigitalSubscription.Disabled;

	return (
		<CardFullMobileModal
			ariaLabel="Allocation request modal"
			modalTitle="Invest now"
			{...rest}
		>
			<div className="grid gap-y-6">
				<AllocationDetails offer={offer} />
				<RenderIfCondition
					condition={!isDigitalSubscriptionDisabled}
					fallback={
						<TenantSupportLink>
							<Button data-testid="contact-us-button" fullWidth>
								Contact Us
							</Button>
						</TenantSupportLink>
					}
				>
					<Button
						data-testid="offer-invest-button"
						fullWidth
						onClick={() => {
							router.push(`/invest/${params.slug}/place-allocation`);
						}}
					>
						Invest
					</Button>
				</RenderIfCondition>
				<CurrentlyInvestingAs />
			</div>
		</CardFullMobileModal>
	);
}
